import React, { useCallback, useMemo } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Skeleton } from "@mui/material";
import Typography from "@mui/material/Typography";
import { actions as AppActions } from "app/Domain/App/Ducks/App.duck";
import employerService from "../../Services/EmployerService";
import { ConfirmDialog } from "app/components/ConfirmDialog";
import { InfoDialog } from "app/components/InfoDialog";
import { normalizeError } from "app/utils";
import { MESSAGE_SEVERITY_ERROR } from "../../../../common";
import TabOutlet from "app/components/TabOutlet";
import { IsOwnerAdmin } from "app/components/Authorization";
import { NotFoundView } from "../../../../views";
import { useDialog } from "../../../../hooks";
import { TranslationKeys } from "../../../../translations";
import { DivHeaderActionsStyled, TypographyStyled } from "./EmployerDetailPage.styles";
import { ActionButton } from "../../../../components";
import { EmployerDetailProvider, useEmployerDetailContext } from "./EmployerDetailContext";
let employerToDelete = null;
const EmployerDetailPageContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { employerDetail: employer, isLoading: loading } = useEmployerDetailContext();
  const {
    dialogState: showDeleteConfirmationDialog,
    closeDialog: closeDeleteConfirmationDialog,
    openDialog: openDeleteConfirmationDialog
  } = useDialog();
  const {
    dialogState: showInfoHasConnectionsDialog,
    closeDialog: closeInfoHasConnectionsDialog,
    openDialog: openInfoHasConnectionsDialog
  } = useDialog();
  const disabledIfNotOwner = !IsOwnerAdmin();
  const tabData = useMemo(() => {
    let tabs = [
      {
        path: "info",
        label: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.employers_tab_general })
      },
      {
        path: "summary",
        label: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.employers_tab_summary })
      },
      {
        path: "employees",
        label: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.employers_tab_employees })
      },
      {
        path: "files",
        label: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.employers_tab_files })
      },
      {
        path: "legal",
        label: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.employers_tab_legal }),
        disabled: true
      }
    ];
    if (employer?.isPensioenbijIntegration) {
      tabs = [
        ...tabs.slice(0, 3),
        {
          path: "pensioenbij",
          label: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.global_pensioenbij })
        },
        ...tabs.slice(3)
      ];
    }
    return tabs;
  }, [employer]);
  const removeEmployer = useCallback(async () => {
    if (employerToDelete) {
      employerService.deleteEmployer(employerToDelete).then(() => {
        navigate("/employers");
      }).catch((err) => {
        const errors = normalizeError(err);
        const severity = MESSAGE_SEVERITY_ERROR;
        const details = {
          method: "deleteEmployer",
          severity,
          message: errors.message
        };
        dispatch(AppActions.displayMessage(details));
      });
    }
  }, [dispatch, navigate]);
  const handleClickDelete = useCallback(() => {
    const hasConnections = employer?.connections?.length === 0;
    employerToDelete = employer?.employerId;
    hasConnections ? openDeleteConfirmationDialog() : openInfoHasConnectionsDialog();
  }, [employer?.connections?.length, employer?.employerId, openDeleteConfirmationDialog, openInfoHasConnectionsDialog]);
  const title = useMemo(() => {
    if (employer) {
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Typography, { variant: "h5" }, employer.employerName), /* @__PURE__ */ React.createElement(TypographyStyled, { variant: "h6" }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: `employers.${employer.status}` })));
    }
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Typography, { variant: "h5" }, /* @__PURE__ */ React.createElement(Skeleton, { variant: "text", width: 200 })), /* @__PURE__ */ React.createElement(Typography, { variant: "h6" }, /* @__PURE__ */ React.createElement(Skeleton, { variant: "text", width: 30 })));
  }, [employer]);
  const deleteButton = useMemo(() => {
    return /* @__PURE__ */ React.createElement(DivHeaderActionsStyled, null, /* @__PURE__ */ React.createElement(
      ActionButton,
      {
        sx: { minHeight: "35px", minWidth: "200px", padding: 0, zIndex: 1e3 },
        messageId: TranslationKeys.employers_delete,
        onClick: handleClickDelete,
        disabled: disabledIfNotOwner,
        variant: "outlined"
      }
    ));
  }, [handleClickDelete, disabledIfNotOwner]);
  const breadcrumb = useMemo(() => {
    return {
      title: employer?.employerName,
      data: {
        titleElement: title,
        otherElements: deleteButton
      }
    };
  }, [employer, title, deleteButton]);
  const exists = loading || !!employer;
  return exists ? /* @__PURE__ */ React.createElement(TabOutlet, { tabs: tabData, breadcrumb }, showDeleteConfirmationDialog && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      title: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.employers_confirmDeleteTitle }),
      open: showDeleteConfirmationDialog,
      onClose: closeDeleteConfirmationDialog,
      onConfirm: removeEmployer
    },
    /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.employers_confirmDeleteMessage })
  ), showInfoHasConnectionsDialog && /* @__PURE__ */ React.createElement(
    InfoDialog,
    {
      title: /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.employers_confirmDeleteTitle }),
      open: showInfoHasConnectionsDialog,
      onClose: closeInfoHasConnectionsDialog,
      type: "warning"
    },
    /* @__PURE__ */ React.createElement(Typography, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: TranslationKeys.employers_hasConnections }))
  )) : /* @__PURE__ */ React.createElement(Routes, null, /* @__PURE__ */ React.createElement(Route, { element: /* @__PURE__ */ React.createElement(NotFoundView, null), path: "*" }));
};
const EmployerDetailPage = () => {
  const { employerId } = useParams();
  return /* @__PURE__ */ React.createElement(EmployerDetailProvider, { employerId }, /* @__PURE__ */ React.createElement(EmployerDetailPageContent, null));
};
export default EmployerDetailPage;
